import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useRequest } from 'ahooks';
import { Form, Input, Space, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resetPassword, sendVerificationCode } from '@/apis/user.api.ts';

import AuthFormLayout from '../-components/AuthFormLayout';
import VerificationCodeInput from '../-components/VerificationCodeInput';
import { useFormValidation } from '../-hooks/useFormValidation';
import type { ResetPasswordFormData } from '../-models';

export const Route = createFileRoute('/auth/reset-password')({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const { getEmailRules, getPasswordRules, validatePasswordMatch } =
    useFormValidation();

  const { run: runSendCode, loading: sendingCode } = useRequest(
    sendVerificationCode,
    {
      manual: true,
    },
  );

  const { run: runReset, loading: resetting } = useRequest(resetPassword, {
    manual: true,
    onSuccess: () => {
      message.success(t('auth.message.resetSuccess'));
    },
    onError: (error) => {
      message.error(t('auth.message.resetFailed') + error.message);
    },
  });

  const handleSendCode = async (email: string) => {
    await runSendCode(email);
  };

  const onFinish = (values: ResetPasswordFormData) => {
    if (!validatePasswordMatch(values.password, values.confirmPassword!)) {
      return;
    }
    runReset(values);
  };

  return (
    <AuthFormLayout<ResetPasswordFormData>
      title={t('auth.resetPassword.title')}
      subtitle={t('auth.resetPassword.subtitle')}
      submitText={t('auth.resetPassword.submit')}
      loading={resetting}
      onFinish={onFinish}
    >
      <Form.Item
        name="account"
        rules={getEmailRules()}
        style={{ marginBottom: 16 }}
      >
        <Input
          size="large"
          prefix={<MailOutlined />}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.form.email.placeholder')}
        />
      </Form.Item>

      <VerificationCodeInput
        email={email}
        onSendCode={handleSendCode}
        loading={sendingCode}
      />

      <Form.Item
        name="password"
        rules={getPasswordRules(6)}
        style={{ marginBottom: 16 }}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined />}
          placeholder={t('auth.form.newPassword.placeholder')}
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        rules={[
          {
            required: true,
            message: t('auth.validation.confirmNewPassword.required'),
          },
        ]}
        style={{ marginBottom: 24 }}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined />}
          placeholder={t('auth.form.confirmNewPassword.placeholder')}
        />
      </Form.Item>

      <Space
        size="middle"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginTop: 8,
        }}
      >
        <Link
          from="/"
          to="/auth/sign-up"
          className="text-blue-600 transition-colors hover:text-blue-800"
        >
          {t('auth.links.signUp')}
        </Link>
        <Link
          from="/"
          to="/auth/sign-in"
          className="text-blue-600 transition-colors hover:text-blue-800"
        >
          {t('auth.links.backToSignIn')}
        </Link>
      </Space>
    </AuthFormLayout>
  );
}
